import { API_BASE_URL, STIP_BASE_URL } from "../../props";
import { useMutation } from "@tanstack/react-query";
import {
  doFileUploadNoAuth,
  doHeadRequest,
  useGet,
  usePost,
  usePut,
  useUpdate,
  useUpload,
} from "../../common/fetchServices";
import { addQueryParams, getQueryParams } from "../../util/fetchUtils";

const getContentUrl = id => `${API_BASE_URL}/admin/media/${id}`;
const getPatchContentUrl = ({ id }) => `${API_BASE_URL}/admin/content/${id}`;
const getContentThumbnailUpdateUrl = ({ id }) =>
  `${API_BASE_URL}/admin/content/${id}/thumbnail`;
const getContentFilesUrl = ({ id }) =>
  `${API_BASE_URL}/admin/content/${id}/content_files`;
const getCompoundChildrenUrl = ({ id }) =>
  `${API_BASE_URL}/admin/content/${id}/compound-children`;
const getContentPagesUrl = id => `${API_BASE_URL}/admin/content/${id}/pages`;
const getFileUploadRequestUrl = () => `${STIP_BASE_URL}/file-upload/request`;
const getCreatorUploadUrl = ({ mediaId }) =>
  `${STIP_BASE_URL}/creator/upload/${mediaId}`;

export const useMedia = (id, enabled = true, refetchOnMount = false) => {
  const url = getContentUrl(id);
  return useGet(["content", { id }], url, enabled, refetchOnMount);
};

export const useContentUpdate = () => {
  return useUpdate(getPatchContentUrl, {
    queriesToInvalidate: [
      ["chapters"],
      ["content", ["id"]],
      ["content-badges", ["id"]],
      ["content-status"],
      ["creator-content", ["id"]],
      ["search"],
    ],
  });
};

export const useUploadContentThumbnail = () => {
  return useUpload(getContentThumbnailUpdateUrl);
};

export const useContentPages = (id, params) => {
  const queryParams = getQueryParams(params);
  const url = getContentPagesUrl(id) + `?${queryParams}`;
  return useGet(["content-pages", { id, url }], url);
};

export const useContentFiles = ({ id, limit = "all" }) => {
  const url = getContentFilesUrl({ id }) + `?limit=${limit}`;
  return useGet(["content-files", { id }], url);
};

export const useUpdateContentFiles = () => {
  return usePut(getContentFilesUrl, {
    queriesToInvalidate: [
      ["chapters", ["id"]],
      ["content-files", ["id"]],
    ],
  });
};

export const useCompoundChildren = id => {
  const url = getCompoundChildrenUrl({ id });
  return useGet(["compound-children", { id }], url);
};

export const useChapters = (id, type, { limit = "all" } = {}) => {
  const baseUrl =
    type === "AUDIOBOOK"
      ? getContentFilesUrl({ id })
      : getCompoundChildrenUrl({ id });

  const url = addQueryParams(baseUrl, { limit });
  return useGet(["chapters", { id }], url);
};

export const useFileUploadRequest = () => {
  return usePost(getFileUploadRequestUrl);
};

export const useUploadS3Direct = () => {
  return useMutation({
    mutationFn: ({ url, fileBlob }) => {
      return doFileUploadNoAuth(url, fileBlob);
    },
  });
};

export const useUpdateContentMediaFile = () => {
  return usePost(getCreatorUploadUrl);
};

/**
 * Checks S3 for existence of pdf
 *
 * Catches any exceptions and returns false
 * @param {*} content
 * @param {string|undefined} content.pdfUrl - URL for file in S3
 * @returns {boolean}
 */
export const hasPdf = async content => {
  const { pdfUrl, mimeType } = content;
  if (pdfUrl && mimeType !== "image/tiff") {
    try {
      const res = await doHeadRequest(pdfUrl);
      if (res.status === 200 && res.statusText === "OK") {
        return true;
      }
    } catch {
      return false;
    }
  }

  return false;
};

export const getOptimizedPdfUrl = async content => {
  const { pdfUrl, mimeType } = content;

  if (pdfUrl && mimeType !== "image/tiff") {
    const optimizedPdfUrl = pdfUrl
      ? pdfUrl.replace("content.pdf", "content_optimized.pdf")
      : "";
    try {
      const res = await doHeadRequest(optimizedPdfUrl);
      const fileExists = res.status === 200 && res.statusText === "OK";
      if (fileExists) {
        return optimizedPdfUrl;
      }

      if (content.type === "CHAPTER") {
        return pdfUrl;
      }

      return "";
    } catch (err) {
      return "";
    }
  }

  return "";
};
